<template>
  <div class="module-page">
    <div class="carousel-viewport" ref="viewportRef">
      <div class="carousel-track" :style="trackStyle">
        <!-- Slide 1: Hero -->
        <section class="slide hero-slide">
          <div class="slide-content">
            <div class="hero-badge">
              <span class="badge-dot"></span>
              核心功能
            </div>
            <h1 class="hero-title">Aspen 智能衡算</h1>
            <p class="hero-desc">
              基于 Aspen Plus 模拟引擎，实现物料衡算与能量衡算全自动化。
              导入 .bkp 文件，一键生成专业 Word 报告。
            </p>
            <div class="hero-actions">
              <button class="btn-primary" @click="$emit('download')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                下载使用
              </button>
              <button class="btn-secondary" @click="goToSlide(1)">
                了解功能
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="hero-visual">
            <div class="code-window">
              <div class="code-header">
                <div class="code-dots">
                  <span></span><span></span><span></span>
                </div>
                <span class="code-title">balance_report.docx</span>
              </div>
              <div class="code-body">
                <div class="code-line"><span class="code-prompt">></span> <span class="code-cmd">Loading:</span> <span class="code-val">func3</span></div>
                <div class="code-line"><span class="code-prompt">></span> <span class="code-str">input: CO2+MEOH.bkp</span></div>
                <div class="code-line"><span class="code-ok">✓</span> Module R001 balanced</div>
                <div class="code-line"><span class="code-ok">✓</span> Module R002 balanced</div>
                <div class="code-line"><span class="code-ok">✓</span> Module T001 balanced</div>
                <div class="code-line"><span class="code-ok">✓</span> Module F001 balanced</div>
                <div class="code-line"><span class="code-prompt">></span> <span class="code-cmd">Report generated</span></div>
              </div>
            </div>
          </div>
        </section>

        <!-- Slide 2: 物料衡算 -->
        <section class="slide feature-slide blue">
          <div class="slide-content">
            <div class="feature-icon-large icon-blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
            <h2 class="feature-title">物料衡算</h2>
            <p class="feature-desc">
              自动解析 .bkp 文件中各操作单元的物流数据，逐股物料进行输入输出平衡校验。
              支持组分、流量、温度、压力等多维度分析，确保物料守恒。
            </p>
            <ul class="feature-list">
              <li>自动识别 Aspen 操作单元</li>
              <li>逐股物流输入输出对比</li>
              <li>多维度组分分析</li>
              <li>偏差自动标记提示</li>
            </ul>
          </div>
        </section>

        <!-- Slide 3: 能量衡算 -->
        <section class="slide feature-slide orange">
          <div class="slide-content">
            <div class="feature-icon-large icon-orange">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h2 class="feature-title">能量衡算</h2>
            <p class="feature-desc">
              基于 Aspen 热力学数据库进行精确焓值计算，自动识别加热器、冷却器、
              反应器等能量交换设备，输出热负荷、效率、损失等多维度指标。
            </p>
            <ul class="feature-list">
              <li>精确焓值计算</li>
              <li>热交换设备识别</li>
              <li>Q值与效率分析</li>
              <li>能量流向可视化</li>
            </ul>
          </div>
        </section>

        <!-- Slide 4: 全流程计算 -->
        <section class="slide feature-slide green">
          <div class="slide-content">
            <div class="feature-icon-large icon-green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06A1.65 1.65 0 0 0 15 19.4"/>
              </svg>
            </div>
            <h2 class="feature-title">全流程计算</h2>
            <p class="feature-desc">
              自动遍历整个工艺流程图，从原料到产品进行拓扑排序与遍历。
              上下游物料连续性校验，全局物料平衡表自动汇总生成。
            </p>
            <ul class="feature-list">
              <li>自动拓扑排序</li>
              <li>上下游连续性校验</li>
              <li>全局平衡表汇总</li>
              <li>异常自动标记</li>
            </ul>
          </div>
        </section>

        <!-- Slide 5: 自定义工段 -->
        <section class="slide feature-slide purple">
          <div class="slide-content">
            <div class="feature-icon-large icon-purple">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <h2 class="feature-title">自定义工段</h2>
            <p class="feature-desc">
              自由选择多个工段及其物流进行定制化衡算计算。
              灵活选择进料/出料物流，支持对比分析与趋势追踪。
            </p>
            <ul class="feature-list">
              <li>工段自由组合</li>
              <li>物流灵活选择</li>
              <li>对比分析功能</li>
              <li>趋势追踪报告</li>
            </ul>
          </div>
        </section>

        <!-- Slide 6: Stats & CTA -->
        <section class="slide stats-slide">
          <div class="slide-content">
            <h2 class="stats-title">为什么选择我们</h2>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-number">100%</div>
                <div class="stat-label">自动化率</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">5+</div>
                <div class="stat-label">衡算模块</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">0</div>
                <div class="stat-label">数据上传</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">DOCX</div>
                <div class="stat-label">专业报告</div>
              </div>
            </div>
            <button class="btn-primary btn-large" @click="$emit('download')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              立即下载使用
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- Navigation -->
    <div class="slide-nav">
      <button
        v-for="(_, index) in 6"
        :key="index"
        class="nav-dot"
        :class="{ active: currentSlide === index }"
        @click="goToSlide(index)"
      ></button>
    </div>

    <button class="nav-arrow prev" @click="prevSlide" :disabled="currentSlide === 0">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <button class="nav-arrow next" @click="nextSlide" :disabled="currentSlide === 5">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const currentSlide = ref(0)
const viewportRef = ref(null)
const isScrolling = ref(false)

const trackStyle = computed(() => ({
  transform: `translateX(-${currentSlide.value * 100}%)`
}))

function goToSlide(index) {
  currentSlide.value = index
}

function nextSlide() {
  if (currentSlide.value < 5) {
    currentSlide.value++
  }
}

function prevSlide() {
  if (currentSlide.value > 0) {
    currentSlide.value--
  }
}

// 键盘导航
function handleKeydown(e) {
  if (e.key === 'ArrowRight') nextSlide()
  if (e.key === 'ArrowLeft') prevSlide()
}

// 滚轮导航
function handleWheel(e) {
  if (isScrolling.value) return
  
  const threshold = 50
  if (e.deltaY > threshold && currentSlide.value < 5) {
    isScrolling.value = true
    nextSlide()
    setTimeout(() => isScrolling.value = false, 800)
  } else if (e.deltaY < -threshold && currentSlide.value > 0) {
    isScrolling.value = true
    prevSlide()
    setTimeout(() => isScrolling.value = false, 800)
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('wheel', handleWheel, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('wheel', handleWheel)
})

defineEmits(['download', 'learnMore'])
</script>

<style scoped>
.module-page {
  height: 100%;
  position: relative;
  overflow: hidden;
  background: #fff;
}

/* ═══ Carousel ═══ */
.carousel-viewport {
  height: 100%;
  overflow: hidden;
}

.carousel-track {
  display: flex;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 64px;
  box-sizing: border-box;
}

.slide-content {
  max-width: 800px;
  text-align: center;
}

/* ═══ Slide 1: Hero ═══ */
.hero-slide {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  background: linear-gradient(180deg, #fff 0%, #f5f5f7 100%);
}

.hero-slide .slide-content {
  text-align: left;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #f0f7ff;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  color: #0066ff;
  margin-bottom: 24px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #00c853;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
}

.hero-title {
  font-size: 56px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #1d1d1f;
  margin: 0 0 24px;
}

.hero-desc {
  font-size: 21px;
  line-height: 1.5;
  color: #86868b;
  margin: 0 0 32px;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 28px;
  background: #0071e3;
  color: #fff;
  border: none;
  border-radius: 980px;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-primary:hover {
  background: #0077ed;
  transform: scale(1.02);
}

.btn-primary svg {
  width: 20px;
  height: 20px;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 16px 24px;
  background: transparent;
  color: #0071e3;
  border: none;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-secondary:hover {
  text-decoration: underline;
}

.btn-secondary svg {
  width: 16px;
  height: 16px;
}

/* Hero Visual */
.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.code-window {
  width: 100%;
  max-width: 420px;
  background: #1d1d1f;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.15);
}

.code-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #2d2d2f;
}

.code-dots {
  display: flex;
  gap: 8px;
}

.code-dots span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.code-dots span:nth-child(1) { background: #ff5f57; }
.code-dots span:nth-child(2) { background: #febc2e; }
.code-dots span:nth-child(3) { background: #28c840; }

.code-title {
  font-size: 12px;
  color: #a1a1a6;
  font-family: 'SF Mono', monospace;
}

.code-body {
  padding: 20px;
  font-family: 'SF Mono', monospace;
  font-size: 13px;
  line-height: 1.8;
}

.code-line { color: #f5f5f7; }
.code-prompt { color: #86868b; }
.code-cmd { color: #ff9f0a; }
.code-val { color: #64d2ff; }
.code-str { color: #32d74b; }
.code-ok { color: #32d74b; margin-right: 6px; }

/* ═══ Feature Slides ═══ */
.feature-slide {
  background: #f5f5f7;
}

.feature-slide.blue { background: linear-gradient(135deg, #f0f7ff 0%, #fff 100%); }
.feature-slide.orange { background: linear-gradient(135deg, #fff4e6 0%, #fff 100%); }
.feature-slide.green { background: linear-gradient(135deg, #f0fff4 0%, #fff 100%); }
.feature-slide.purple { background: linear-gradient(135deg, #f3f0ff 0%, #fff 100%); }

.feature-icon-large {
  width: 96px;
  height: 96px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
}

.feature-icon-large svg {
  width: 44px;
  height: 44px;
}

.icon-blue { background: #e8f4ff; color: #0071e3; }
.icon-orange { background: #fff0db; color: #ff9500; }
.icon-green { background: #e6f9ed; color: #00c853; }
.icon-purple { background: #f0ebff; color: #af52de; }

.feature-title {
  font-size: 48px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 20px;
  letter-spacing: -0.02em;
}

.feature-desc {
  font-size: 20px;
  line-height: 1.6;
  color: #86868b;
  margin: 0 0 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.feature-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 100px;
  font-size: 15px;
  color: #1d1d1f;
  font-weight: 500;
}

/* ═══ Stats Slide ═══ */
.stats-slide {
  background: linear-gradient(180deg, #f5f5f7 0%, #fff 100%);
}

.stats-title {
  font-size: 40px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 48px;
  letter-spacing: -0.015em;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 48px;
}

.stat-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.stat-number {
  font-size: 36px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 15px;
  color: #86868b;
  font-weight: 500;
}

.btn-large {
  padding: 18px 36px;
  font-size: 18px;
}

/* ═══ Navigation ═══ */
.slide-nav {
  position: fixed;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 100;
}

.nav-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: #d2d2d7;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.nav-dot.active {
  background: #0071e3;
  transform: scale(1.3);
}

.nav-arrow {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #fff;
  color: #1d1d1f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 100;
}

.nav-arrow:hover:not(:disabled) {
  background: #0071e3;
  color: #fff;
  transform: translateY(-50%) scale(1.05);
}

.nav-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-arrow svg {
  width: 20px;
  height: 20px;
}

.nav-arrow.prev { left: 32px; }
.nav-arrow.next { right: 32px; }

/* ═══ Responsive ═══ */
@media (max-width: 1024px) {
  .hero-slide {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hero-slide .slide-content {
    text-align: center;
  }

  .hero-actions {
    justify-content: center;
  }

  .hero-title {
    font-size: 42px;
  }

  .feature-title {
    font-size: 36px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .nav-arrow {
    display: none;
  }

  .slide-nav {
    right: 16px;
  }
}

@media (max-width: 768px) {
  .slide {
    padding: 60px 24px;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-desc {
    font-size: 18px;
  }

  .feature-title {
    font-size: 28px;
  }

  .feature-desc {
    font-size: 17px;
  }

  .stats-title {
    font-size: 28px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-card {
    padding: 24px 16px;
  }

  .stat-number {
    font-size: 28px;
  }

  .slide-nav {
    bottom: 24px;
    right: 50%;
    top: auto;
    transform: translateX(50%);
    flex-direction: row;
  }
}
</style>
